import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import PaymentModal from './PaymentModal';
import ErrorModal from './ErrorModal';
import LightCreditsModal from './LightCreditsModal';
import ClubTermsModal from './ClubTermsModal';
import { getClubTerms } from '../api';
import { renewMembership, purchaseCredits, grantAccess } from '../api';
import { buildCreditReference, buildRenewReference } from '../utils/paymentReference';

/** Minimal parse for club `accessControl` from API (JSON string or object). Mirrors BookingDetailsModal. */
const parseClubAccessControl = (raw) => {
  if (raw == null || raw === '') return null;
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (obj.type === 'access_code') return { type: 'access_code' };
  if (obj.type === 'shelly') return { type: 'shelly' };
  return null;
};

/** Single generic copy for grantAccess failures (details go to console for support). */
const GRANT_ACCESS_USER_MESSAGE = 'An error occurred. Please contact the club to gain access.';

const Sidebar = ({ isOpen, onClose }) => {
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logoutUser, loginUser } = React.useContext(AuthContext);
  const { clubDetails, setClubDetails, courtData } = useClub();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showLightCreditsModal, setShowLightCreditsModal] = useState(false);
  const [showClubTermsModal, setShowClubTermsModal] = useState(false);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [clubTermsHtml, setClubTermsHtml] = useState(null);

  // Membership-based club access (gain access at any time from the sidebar)
  const [revealedAccessCode, setRevealedAccessCode] = useState(null);
  const [accessCodeLoading, setAccessCodeLoading] = useState(false);
  const [openGateLoading, setOpenGateLoading] = useState(false);
  const [gateCooldownActive, setGateCooldownActive] = useState(false);
  const [grantAccessInlineError, setGrantAccessInlineError] = useState('');

  const accessControl = parseClubAccessControl(clubDetails?.accessControl);
  const showClubAccess = Boolean(accessControl && user?.membership?.allowAccess === true);
  const showPublishedMemberships = user?.hasPublishedMemberships === true
    || clubDetails?.hasPublishedMemberships === true;

  // Reset the access UI whenever the sidebar is closed
  useEffect(() => {
    if (!isOpen) {
      setRevealedAccessCode(null);
      setAccessCodeLoading(false);
      setOpenGateLoading(false);
      setGateCooldownActive(false);
      setGrantAccessInlineError('');
    }
  }, [isOpen]);

  const handleAccessCodeClick = async () => {
    if (revealedAccessCode !== null) return;
    try {
      setGrantAccessInlineError('');
      setAccessCodeLoading(true);
      const res = await grantAccess();
      if (res?.outcome === 'access_code' && res.code) {
        setRevealedAccessCode(res.code);
      } else {
        console.error('[grantAccess] access code unexpected response', res);
        setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
      }
    } catch (err) {
      console.error('[grantAccess] access code request failed', err?.response?.data || err);
      setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
    } finally {
      setAccessCodeLoading(false);
    }
  };

  const handleOpenGateClick = async () => {
    try {
      setGrantAccessInlineError('');
      setOpenGateLoading(true);
      const res = await grantAccess();
      if (res?.outcome === 'shelly' && res.success) {
        setGrantAccessInlineError('');
      } else {
        console.error('[grantAccess] open gate unexpected response', res);
        setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
      }
    } catch (err) {
      console.error('[grantAccess] open gate request failed', err?.response?.data || err);
      setGrantAccessInlineError(GRANT_ACCESS_USER_MESSAGE);
    } finally {
      setOpenGateLoading(false);
      setGateCooldownActive(true);
      window.setTimeout(() => setGateCooldownActive(false), 1000);
    }
  };

  const handleLogout = () => {
    logoutUser();
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Helper function to get membership status
  const getMembershipStatus = () => {
    if (!user?.expiryDate) return { status: 'none', color: 'green' };
    
    const expiryDate = new Date(user.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'red' };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', color: 'amber' };
    } else {
      return { status: 'active', color: 'green' };
    }
  };

  // Helper function to format expiry date
  const formatExpiryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    
    // Check if the date is today (same day, month, and year)
    if (date.toDateString() === today.toDateString()) {
      return 'today!';
    }
    
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Projected expiry after renewal (same setMonth logic as backend processMembershipRenewal).
  const getRenewalTermBulletHtml = () => {
    const term = Number(user?.membership?.term);
    if (!user?.expiryDate || !Number.isFinite(term) || term <= 0) return '';
    const projected = new Date(user.expiryDate);
    projected.setMonth(projected.getMonth() + term);
    const newExpiryDate = formatExpiryDate(projected.toISOString());
    const monthWord = term === 1 ? 'month' : 'months';
    return `<li>Renew for <span class="font-bold">${term} ${monthWord}</span>, valid until <span class="font-bold">${newExpiryDate}</span></li>`;
  };

  // Helper function to format currency
  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2
      }).format(numericValue);
    } catch {
      return `R${numericValue.toFixed(2)}`;
    }
  };

  const membershipStatus = getMembershipStatus();

  // Check what renewal action should be shown (if any)
  const getRenewalAction = () => {
    if (!user?.expiryDate) return null;
    
    const expiryDate = new Date(user.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    // Only show renewal options if expired or expires within 30 days
    if (daysUntilExpiry > 30) return null;
    
    // Check if online renewal is available
    if (user?.membership?.zapperFee !== undefined) {
      return 'renew_button';
    } else {
      return 'contact_club';
    }
  };

  const renewalAction = getRenewalAction();

  // Check if renew button should be shown
  const shouldShowRenewButton = () => {
    return renewalAction === 'renew_button';
  };

  // Check if contact club message should be shown
  const shouldShowContactClubMessage = () => {
    return renewalAction === 'contact_club';
  };

  const handleRenewClick = () => {
    if (!user?.membership) return;
    
    const totalPaymentAmount = (user.membership.fee || 0) + (user.membership.zapperFee || 0);
    const reference = buildRenewReference(user.firstName, user.lastName);
    
    setPaymentDetails({
      totalPaymentAmount,
      reference,
      userUuid: user.uuid,
      userDisplayName: `${user.firstName} ${user.lastName}`
    });
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async () => {
    try {
      const response = await renewMembership(paymentDetails.reference);

      if (response?.token) {
        loginUser(response.token);
      }
    } catch (error) {
      console.error('Error renewing membership:', error);
      // Close payment modal first to prevent multiple QR codes
      setShowPaymentModal(false);
      // Show error modal to user with reference
      setErrorMessage(`Failed to renew membership, please contact the club with Reference: ${paymentDetails.reference}`);
      setShowErrorModal(true);
    }
  };

  const handleCreditPurchaseClick = (amount) => {
    if (!user) return;
    
    const reference = buildCreditReference(user.firstName, user.lastName);
    
    setPaymentDetails({
      totalPaymentAmount: amount,
      reference,
      userUuid: user.uuid,
      userDisplayName: `${user.firstName} ${user.lastName}`,
      type: 'credit_purchase',
      creditAmount: amount
    });
    setShowPaymentModal(true);
  };

  const handleCreditPaymentComplete = async () => {
    try {
      // Call the purchase credits API with reference and credit amount
      const response = await purchaseCredits(paymentDetails.reference, paymentDetails.creditAmount);
      
      // Update the token and user state without refresh
      if (response?.token) {
        loginUser(response.token);
      }
      
      // Don't auto-refresh - let user close modal manually like booking flow
      // The token will be updated and used on next page refresh
    } catch (error) {
      console.error('Error purchasing credits:', error);
      // Close payment modal first to prevent multiple QR codes
      setShowPaymentModal(false);
      // Show error modal to user with reference
      setErrorMessage(`Failed to purchase credits, please contact the club with Reference: ${paymentDetails.reference}`);
      setShowErrorModal(true);
    }
  };

  const handlePaymentModalClose = () => {
    setPaymentDetails(null);
    setShowPaymentModal(false);
  };

  const handleMenuClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.path);
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (sidebarRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose]);

  // Fetch club terms HTML when modal is opened
  React.useEffect(() => {
    const fetchTermsHtml = async () => {
      if (!showClubTermsModal || clubTermsHtml !== null || isLoadingTerms) return;
      try {
        setIsLoadingTerms(true);
        const terms = await getClubTerms();
        setClubTermsHtml(terms?.html || '');
      } catch (err) {
        console.warn('Unable to load club terms', err);
        setClubTermsHtml('');
      } finally {
        setIsLoadingTerms(false);
      }
    };
    fetchTermsHtml();
  }, [showClubTermsModal, clubTermsHtml, isLoadingTerms]);

  const menuItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        </svg>
      ),
      show: true
    },
    {
      path: '/club-admin',
      label: 'Admin',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.108 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.11v1.093c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.142.854.108 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.11v-1.094c0-.55.398-1.019.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.142-.854-.108-1.204l-.527-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      ),
      show: user?.permissions?.clubAdmin?.canView
    },
    {
      path: '/club-config',
      label: 'Config',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75a4.5 4.5 0 0 1-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 1 1-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 0 1 6.336-4.486l-3.276 3.276a3.004 3.004 0 0 0 2.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.867 19.125h.008v.008h-.008v-.008Z" />
        </svg>
      ),
      show: user?.permissions?.clubConfiguration?.canView
    },
    {
      path: '/analytics',
      label: 'Analytics',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 15v-4m5 4V7m5 8v-2" />
        </svg>
      ),
      show: user?.permissions?.clubAdmin?.canView
    },
    {
      path: '#club-terms',
      label: 'Club Terms',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-5.5c0-.621-.504-1.125-1.125-1.125h-4.25L12.25 6h-6.5c-.621 0-1.125.504-1.125 1.125v9.75c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 10.5h6m-6 3h4" />
        </svg>
      ),
      show: clubDetails?.termsEnabled,
      onClick: () => setShowClubTermsModal(true)
    },
    {
      path: '/memberships',
      label: 'Memberships',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.941 3.199.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.479m.941-3.199A5.971 5.971 0 0 0 6 18.72M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
        </svg>
      ),
      show: showPublishedMemberships
    },
    // Contact Club button
    {
      path: '/club-contacts',
      label: 'Contact Club',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
        </svg>
      ),
      show: true
    }
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-600 hover:text-slate-800 focus:outline-none"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Membership Status Section */}
        {user?.userType && (
          <div className="px-4 py-3 sm:py-4 border-b border-slate-300">
            <div className="text-center">
              <div className="text-base font-medium text-slate-800 mb-2">
                {user.firstName} {user.lastName}
              </div>
              <div className="flex items-center justify-center mb-2">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  membershipStatus.color === 'green' ? 'bg-green-500' :
                  membershipStatus.color === 'amber' ? 'bg-orange-400' :
                  membershipStatus.color === 'red' ? 'bg-red-600' :
                  'bg-slate-400'
                }`}></div>
                <span className="text-sm font-medium text-slate-800">
                  {user.userType}
                </span>
              </div>
                             {user.expiryDate && (
                 <div className="text-xs text-slate-600">
                   {membershipStatus.status === 'expired' ? 'Expired ' : 'Renew by '}
                   {formatExpiryDate(user.expiryDate)}
                 </div>
               )}
               {shouldShowRenewButton() && (
                 <button
                   onClick={handleRenewClick}
                   className="mt-2 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                 >
                   Renew Now
                 </button>
               )}
               {shouldShowContactClubMessage() && (
                 <div className="mt-2 text-xs text-slate-600">
                   <Link to="/club-contacts" className="text-blue-600 hover:text-blue-800 underline">Contact Club</Link> to renew
                 </div>
               )}
            </div>
          </div>
        )}

        {/* Club Access — members whose membership allows access can gain entry at any time */}
        {showClubAccess && (
          <div className="px-4 py-3 sm:py-4 border-b border-slate-300">
            <div className="text-center">
              <div className="text-xs text-slate-600 mb-2">
                Club Access
              </div>
              {accessControl.type === 'access_code' ? (
                <>
                  <button
                    type="button"
                    onClick={handleAccessCodeClick}
                    disabled={revealedAccessCode !== null || accessCodeLoading || Boolean(grantAccessInlineError)}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:cursor-not-allowed disabled:bg-blue-400 disabled:hover:bg-blue-400"
                  >
                    {accessCodeLoading
                      ? 'Loading…'
                      : revealedAccessCode !== null
                        ? 'Access Code'
                        : 'Show Access Code'}
                  </button>
                  {revealedAccessCode && (
                    <div
                      className="mt-2 select-all break-all font-mono text-lg font-semibold tracking-wide text-green-600"
                      aria-live="polite"
                    >
                      {revealedAccessCode}
                    </div>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleOpenGateClick}
                  disabled={openGateLoading || gateCooldownActive || Boolean(grantAccessInlineError)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:cursor-not-allowed disabled:bg-blue-400 disabled:hover:bg-blue-400"
                >
                  {openGateLoading ? 'Opening…' : 'Open Gate'}
                </button>
              )}
              {grantAccessInlineError && (
                <div role="alert" className="mt-2 text-xs text-red-600">
                  {grantAccessInlineError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Credits — hidden when club has no payment provider (members-only / no online payments) */}
        {clubDetails?.paymentProvider !== 'none' && (
          <div className="px-4 py-3 sm:py-4 border-b border-slate-300">
            <div className="text-center">
              <div className="text-base font-medium text-slate-800 mb-2">
                {formatCurrency(user?.credits || 0)}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowLightCreditsModal(true);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Buy Credits
              </button>
            </div>
          </div>
        )}

        {/* Menu items */}
        <div className="pt-3 pb-3 sm:pt-4 sm:pb-4">
          {menuItems.map((item) => (
            item.show && (
              <button
                key={item.path}
                onClick={() => handleMenuClick(item)}
                className={`w-full flex items-center px-4 py-2 sm:py-3 text-slate-800 hover:bg-slate-100 transition-colors ${
                  location.pathname === item.path ? 'bg-slate-100' : ''
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            )
          ))}

          {/* Separator above Logout */}
          <hr className="my-2 border-slate-300" />

          {/* Logout button */}
          <button
            onClick={() => {
              handleLogout();
              onClose();
            }}
            className="w-full flex items-center px-4 py-2 sm:py-3 text-slate-800 hover:bg-slate-100 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5 mr-3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        totalPaymentAmount={paymentDetails?.totalPaymentAmount || 0}
        reference={paymentDetails?.reference || ''}
        onPaymentComplete={paymentDetails?.type === 'credit_purchase' ? handleCreditPaymentComplete : handlePaymentComplete}
        paymentDetails={paymentDetails}
        customText={paymentDetails?.type === 'credit_purchase' ? `
          <ul class="list-disc pl-5">
            <li>Do not close this window until payment completes</li>
            <li>Reference: <span class="font-bold break-all">${paymentDetails?.reference || ''}</span></li>
          </ul>
        ` : `
          <ul class="list-disc pl-5">
            <li>Do not close this window until payment completes</li>
            ${getRenewalTermBulletHtml()}
            <li>Contact the club to renew by alternative means</li>
            <li>Reference: <span class="font-bold break-all">${paymentDetails?.reference || ''}</span></li>
          </ul>
        `}
        showTimer={false}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errorMessage={errorMessage}
      />

      <LightCreditsModal
        isOpen={showLightCreditsModal}
        onClose={() => setShowLightCreditsModal(false)}
        user={user}
        courtData={courtData}
        onSubmit={(amount) => {
          handleCreditPurchaseClick(amount);
        }}
      />

      <ClubTermsModal
        isOpen={showClubTermsModal}
        onClose={() => {
          setShowClubTermsModal(false);
          // Reset terms HTML when modal closes to refetch on next open
          setClubTermsHtml(null);
        }}
        htmlContent={clubTermsHtml || ''}
        title="Club Terms"
      />
    </>
  );
};

export default Sidebar; 